/**
 * Authentication Context
 * Manages user authentication state and login/logout actions
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { firebaseAuth } from '../config/firebase';
import { getAuthToken, setAuthToken, removeAuthToken } from '../api/client';
import { realAPI } from '../api/realEndpoints';
import { useApp } from './AppContext';
import type { User, LoginRequest } from '../api/types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest, rememberMe?: boolean) => Promise<void>;
  logout: () => void;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  updateProfile: (data: { name?: string; email?: string; username?: string; image?: string }) => Promise<void>;
  fetchAdminProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { preloadDashboardData, clearDashboardData } = useApp();

  // Load stored user & remember-me details
  useEffect(() => {
    const token = getAuthToken();

    const savedUser =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData");

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }

    setIsLoading(false);
  }, []);

  const login = async (credentials: LoginRequest, rememberMe = false) => {
    try {
      const response = await firebaseAuth.signIn(credentials.email, credentials.password);

      // Save token
      setAuthToken(response.token);

      const userData = {
        ...response.user,
        role: "admin" as const
      };

      setUser(userData);

      if (rememberMe) {
        // Save user and login details permanently
        localStorage.setItem("userData", JSON.stringify(userData));
        localStorage.setItem("rememberMe", "true");

        // Save login credentials for auto-fill
        localStorage.setItem("savedEmail", credentials.email);
        localStorage.setItem("savedPassword", credentials.password);

      } else {
        // Only temporary session
        sessionStorage.setItem("userData", JSON.stringify(userData));
      }

      // Preload dashboard graph data in background for better UX
      preloadDashboardData().catch(err => {
        console.error('Failed to preload dashboard data:', err);
      });

    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await firebaseAuth.signOut();
    } catch (error) {
      console.error("Firebase sign out error:", error);
    }

    // Remove token only
    removeAuthToken();

    // ❗ DO NOT remove login details if RememberMe=true
    if (localStorage.getItem("rememberMe") !== "true") {
      localStorage.removeItem("savedEmail");
      localStorage.removeItem("savedPassword");
    }

    // Always remove active user session
    localStorage.removeItem("userData");
    sessionStorage.removeItem("userData");

    // Clear preloaded dashboard data
    clearDashboardData();

    setUser(null);
  };

  const updatePassword = async (currentPassword: string, newPassword: string) => {
    try {
      await firebaseAuth.updatePassword(currentPassword, newPassword);
    } catch (error) {
      throw error;
    }
  };

  const updateProfile = async (data: { name?: string; email?: string }) => {
    try {
      if (!user) throw new Error("No authenticated user found");

      // Update using real API (not Firebase)
      await realAPI.users.updateUser(user.id, data);

      // Update local state
      const updatedUser = { ...user, ...data };
      setUser(updatedUser);

      const userDataStr = JSON.stringify(updatedUser);

      if (localStorage.getItem("rememberMe") === "true") {
        localStorage.setItem("userData", userDataStr);
      } else {
        sessionStorage.setItem("userData", userDataStr);
      }
    } catch (error) {
      throw error;
    }
  };

  const fetchAdminProfile = useCallback(async () => {
    try {
      const profileData = await realAPI.users.getProfile();

      // Update user state with admin profile data
      if (!user) return;

      const updatedUser: User = {
        id: profileData.id.toString(), // Convert number id to string
        name: profileData.name || user.name,
        email: profileData.email || user.email,
        username: profileData.username || user.username,
        image: profileData.image || user.image,
        role: profileData.role || user.role, // Use role from API response
      };

      setUser(updatedUser);

      // Update stored user data
      const userDataStr = JSON.stringify(updatedUser);
      if (localStorage.getItem("rememberMe") === "true") {
        localStorage.setItem("userData", userDataStr);
      } else {
        sessionStorage.setItem("userData", userDataStr);
      }
    } catch (error) {
      console.error('Failed to fetch admin profile:', error);
      throw error;
    }
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        updatePassword,
        updateProfile,
        fetchAdminProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
